import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue'
import { computed, ref, toValue } from 'vue'
import { EMPTY_SEO_BLOCK } from '~/constants/empty'
import type { ArticleDataResponse, ArticleData } from '~/types/article'
import type { BlogArticleObj, PartnerObj, SeoSiblingsBlock } from '~/types/school'
import { getAxiosResponseStatus } from '~/utils/axios'
import { parseBlock } from '~/utils/helpers'

export type UseArticleDataOptions = {
  alias?: MaybeRefOrGetter<string | undefined>
  language?: MaybeRefOrGetter<string | undefined>
}

/**
 * Charge un article blog via `/api/blog/articledata` (SSR via `useAsyncData`).
 * @param {UseArticleDataOptions} options Alias et langue de l'article.
 * @returns {Promise<{ articleData: Ref<ArticleDataResponse | null>; loading: ComputedRef<boolean>; error: ComputedRef<string | null>; lastHttpStatus: ComputedRef<number | undefined>; refresh: () => Promise<void> }>} Données et état de chargement.
 */
export const useArticleData = async (options: UseArticleDataOptions) => {
  const api = useFrontApi()
  const lastHttpStatus: Ref<number | undefined> = ref(undefined)

  const {
    data: articleData,
    error,
    status,
    refresh,
  } = await useAsyncData<ArticleDataResponse | null>(
    () => `article-data-${toValue(options.language) ?? ''}-${toValue(options.alias) ?? ''}`,
    async () => {
      const alias: string | undefined = toValue(options.alias)
      const language: string | undefined = toValue(options.language)

      if (!alias) {
        lastHttpStatus.value = undefined
        return null
      }

      const segments: string[] = alias.split('/')

      if (segments.length > 1) {
        console.warn(`Alias inattendu : ${alias}. Fetch annulé.`)
        return null
      }
      if (segments.length !== 1) return null

      try {
        const endpoint: string =
          `/api/blog/articledata?alias=${encodeURIComponent(alias)}` +
          (language ? `&language=${encodeURIComponent(language)}` : '')
        const { data } = await api.get<ArticleData>(endpoint)
        lastHttpStatus.value = 200

        return {
          articledatas: data,
          parsedParentBlock: parseBlock<SeoSiblingsBlock>(data.parentBlock, EMPTY_SEO_BLOCK),
          parsedPartners: parseBlock<PartnerObj[]>(data.partners, []),
          parsedArticles: parseBlock<BlogArticleObj[]>(data.otherArticlesCards, []),
        }
      } catch (requestError: unknown) {
        lastHttpStatus.value = getAxiosResponseStatus(requestError)
        return null
      }
    },
    {
      watch: [() => toValue(options.alias), () => toValue(options.language)],
    },
  )

  const loading: ComputedRef<boolean> = computed<boolean>(() => status.value === 'pending')

  const errorMessage: ComputedRef<string | null> = computed<string | null>(() =>
    error.value ? (error.value.message ?? 'Erreur inconnue') : null,
  )

  return {
    articleData,
    loading,
    error: errorMessage,
    lastHttpStatus: computed((): number | undefined => lastHttpStatus.value),
    refresh,
  }
}
